
import {defineComponent, nextTick, onMounted, reactive, ref} from 'vue';
import {SingleSignOnCredentials} from "@dex/squeeze-client-ts";
import {ClientManager} from "@/singletons/ClientManager";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {ToastManager} from "@/util/ToastManager";
import InputText from "primevue/inputtext";
import Password from 'primevue/password';
import {useVuelidate} from "@vuelidate/core";
import {required} from "@vuelidate/validators";

export default defineComponent({
	name: "SystemMicrosoftAuthentication",
	components: {
		InputText,
		Password,
	},
	setup() {
		const {t} = useI18n();
		const toast = useToast();

		/** Api for authentication */
		const authenticationApi = ClientManager.getInstance().squeeze.authentication;

		/** Show Loading on load data */
		const loading = ref<boolean>(false);

		/** Object with Microsoft authentication credentials */
		const systemMicrosoftAuthentication = reactive<SingleSignOnCredentials>({
			clientId:	undefined,
			clientSecret: undefined,
		});

		/** Determines the required rules for validation */
		const rules = {
			clientId: { required },
			clientSecret: { required },
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, systemMicrosoftAuthentication);

		/** Triggered when (all) field values are invalid */
		const showErrorMessage = ref<boolean>(false);

		/** Get Microsoft authentication credentials */
		const getMicrosoftAuthentication = () => {
			loading.value = true;
			authenticationApi.getSingleSignOnCredentials()
				.then(data => {
					Object.assign(systemMicrosoftAuthentication, data);
				}).catch(response => response.json().then ((error: { message: string; statusCode: number }) => {
					if (error.statusCode === 500) {
						return;
					}
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + error.message);
				})).finally(() => {
					loading.value = false;
				})
		}

		/**
		 * Save microsoft authentication credentials
		 * If the “resetCredentials” parameter is set to “true”, the credentials will be reset
		 * @param event
		 * @param resetCredentials (optional)
		 */
		const saveMicrosoftAuthenticationCredentials = (event: any, resetCredentials: boolean = false) => {
			if (v$.value.$invalid && showErrorMessage.value && !resetCredentials) {
				return;
			}
			loading.value = true;

			// set the credentials to an empty string, when "resetCredentials" is true
			if (resetCredentials) {
				systemMicrosoftAuthentication.clientId = '';
				systemMicrosoftAuthentication.clientSecret = '';
			}

			authenticationApi.setSingleSignOnCredentials(systemMicrosoftAuthentication)
				.then(() => {
					const messageKey = resetCredentials
						? 'Squeeze.System.ResetMicrosoftAuthenticationCredentials'
						: 'Squeeze.System.SaveMicrosoftAuthenticationCredentials';

					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t(messageKey));
				})
				.catch(response => response.json().then ((error: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + error.message);
				})).finally(() => {
					loading.value = false;
				})
		}

		/** Triggered when change in input-field */
		const update = async () => {
			await nextTick(); // Otherwise the change of the fields will not be recognised fast enough
			showErrorMessage.value = v$.value.$invalid;
		}

		onMounted(() => {
			getMicrosoftAuthentication();
		})

		return {
			loading,
			systemMicrosoftAuthentication,
			v$,
			showErrorMessage,
			saveMicrosoftAuthenticationCredentials,
			update,
		};
	},
});

